import React from 'react';
import { FiMapPin, FiGlobe } from 'react-icons/fi';

/**
 * LocationBadge - Componente para mostrar la ubicación geográfica de un login
 * con bandera del país y formato legible
 */
const LocationBadge = ({ loginRecord, showFullLocation = true, className = '' }) => {
  const {
    country,
    country_code,
    city,
    region,
    ip_address,
    timezone
  } = loginRecord;

  // Si no hay datos de geolocalización, mostrar solo IP
  if (!country && !city) {
    return (
      <div className={`flex items-center space-x-2 text-sm text-gray-600 ${className}`}>
        <FiGlobe className="h-4 w-4 text-gray-400" />
        <span className="font-mono">{ip_address || 'Desconocida'}</span>
      </div>
    );
  }

  // Construir la cadena de ubicación
  const buildLocationString = () => {
    if (showFullLocation) {
      // Formato completo: "Madrid, Madrid, Spain"
      return [city, region, country].filter(Boolean).join(', ');
    } else {
      // Formato corto: "Madrid, Spain" o solo "Spain"
      return city && country ? `${city}, ${country}` : country;
    }
  };

  const location = buildLocationString();

  // URL de la bandera usando flagcdn.com (servicio gratuito y rápido)
  const getFlagUrl = () => {
    if (!country_code) return null;

    // Usar diferentes tamaños según el caso
    const size = showFullLocation ? '20x15' : '16x12';
    return `https://flagcdn.com/${size}/${country_code.toLowerCase()}.png`;
  };

  const flagUrl = getFlagUrl();

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Bandera del país */}
      {flagUrl && (
        <img
          src={flagUrl}
          alt={`${country} flag`}
          className="inline-block rounded-sm shadow-sm"
          title={country}
          onError={(e) => {
            // Si falla la carga de la bandera, ocultarla
            e.target.style.display = 'none';
          }}
        />
      )}

      {/* Icono de ubicación */}
      <FiMapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />

      {/* Texto de ubicación */}
      <span className="text-sm text-gray-700">
        {location}
      </span>

      {/* Timezone como tooltip (opcional) */}
      {timezone && showFullLocation && (
        <span
          className="text-xs text-gray-400 ml-1"
          title={`Zona horaria: ${timezone}`}
        >
          ({timezone})
        </span>
      )}
    </div>
  );
};

/**
 * LocationBadgeCompact - Versión compacta para usar en tablas
 */
export const LocationBadgeCompact = ({ loginRecord, className = '' }) => {
  return (
    <LocationBadge
      loginRecord={loginRecord}
      showFullLocation={false}
      className={className}
    />
  );
};

/**
 * LocationBadgeWithIP - Versión que muestra ubicación + IP
 */
export const LocationBadgeWithIP = ({ loginRecord, className = '' }) => {
  const { ip_address } = loginRecord;

  return (
    <div className={`space-y-1 ${className}`}>
      <LocationBadge loginRecord={loginRecord} showFullLocation={true} />

      {ip_address && (
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <FiGlobe className="h-3 w-3 text-gray-400" />
          <span className="font-mono">{ip_address}</span>
        </div>
      )}
    </div>
  );
};

export default LocationBadge;
